import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { BodyText, Button, ColorDot, Div, Flex, Gutter, Icon, Input } from '@gaiads/telia-react-component-library';
import { AdminPropertyValue, LocalProperty, LocalPropertyValue } from '@cdaas/api-clients/dist/product-service-openapi';
import { Card } from '@cdaas/reusable-components-daas';

import { addPropertyValue } from '../../../../store/adminProperties';
import { setNotification } from '../../../../store/ui';
import { productServiceAdminPropertyValuesClient } from '../../../../api-client/product-service-client';

interface PropertyItemProps {
  readonly property: LocalProperty;
}

const HEX_PATTERN = /^[0-9a-f]{6}$/i;

// Stored without leading '#', matching existing values (e.g. FF3B11)
const normalizeHex = (hex: string) => hex.trim().replace(/^#/, '').toUpperCase();

const typeLabel: Record<string, string> = {
  color: 'properties.types.color',
  text: 'properties.types.text',
  number: 'properties.types.number',
};

const ColorPropertyValue: React.FC<{ readonly value: LocalPropertyValue }> = ({ value }) => {
  const colorName = value.value ?? '';
  const hexCodeRaw = value.internal_value ?? '';
  const hexCode = hexCodeRaw.startsWith('#') ? hexCodeRaw : `#${hexCodeRaw}`;
  const isWhite = colorName.toLowerCase() === 'white' || ['#ffffff', '#fff'].includes(hexCode.toLowerCase());

  const labelText = value.internal_value ? `${value.value} (${value.internal_value})` : value.value;

  return (
    <Flex centeredVertically margin={{ right: 'sm', bottom: 'xs' }}>
      {hexCodeRaw && (
        <>
          <ColorDot color={hexCode} withBorder={isWhite} />
          <Gutter size="xs" />
        </>
      )}
      <BodyText color="gray700">{labelText}</BodyText>
    </Flex>
  );
};

const TextPropertyValue: React.FC<{ readonly value: LocalPropertyValue }> = ({ value }) => (
  <Flex centeredVertically>
    <BodyText color="gray700">{value.value}</BodyText>
    <Gutter size="xs" />
  </Flex>
);

export default function PropertyItem({ property }: PropertyItemProps) {
  const { t } = useTranslation('adminSettings');

  const propertyValues = property.property_values ?? [];
  const hasValues = propertyValues.length > 0;
  const dispatch = useDispatch();
  const isColor = property.type === 'color';
  const sortedColorValues = [...propertyValues].sort((a, b) => (a.value ?? '').localeCompare(b.value ?? '', undefined, { sensitivity: 'base' }));
  const resolvedTypeLabel = t(typeLabel[property.type] ?? property.type);

  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [newInternalValue, setNewInternalValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const normalizedHex = normalizeHex(newInternalValue);
  const isHexValid = HEX_PATTERN.test(normalizedHex);
  const hexError = normalizedHex && !isHexValid ? t('properties.errors.invalidHex') : undefined;
  const trimmedValue = newValue.trim();
  const isDuplicateName =
    !!trimmedValue && propertyValues.some(v => (v.value ?? '').trim().localeCompare(trimmedValue, undefined, { sensitivity: 'base' }) === 0);
  const nameError = isDuplicateName ? t('properties.errors.duplicateName') : undefined;
  const canSave = !!trimmedValue && !isDuplicateName && isHexValid && !isSaving;

  const handleAddStart = () => {
    setNewValue('');
    setNewInternalValue('');
    setIsAdding(true);
  };

  const handleAddCancel = () => {
    setIsAdding(false);
  };

  const createPropertyValue = async (value: string, internalValue?: string): Promise<boolean> => {
    const payload: AdminPropertyValue = {
      id: 0,
      property_id: property.id,
      value,
      internal_value: internalValue,
    };
    setIsSaving(true);
    try {
      const response = await productServiceAdminPropertyValuesClient.postAdminPropertyValue(property.id, payload);
      dispatch(addPropertyValue({ propertyId: property.id, value: response.data }));
      return true;
    } catch {
      dispatch(setNotification({ text: t('properties.errors.createValue'), show: true, type: 'warning' }));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSave = async () => {
    if (!canSave) return;
    if (await createPropertyValue(trimmedValue, normalizedHex)) {
      setIsAdding(false);
    }
  };

  return (
    <div data-testid="property-item">
      <Card title={property.name} alwaysOpenNoChevron>
        <BodyText color="gray700">
          {t('properties.typeLabel')}: {resolvedTypeLabel}
        </BodyText>

        {hasValues && (
          <>
            <Gutter size="sm" />
            <BodyText color="gray700" margin={{ bottom: 'xs' }}>
              {t('properties.valuesLabel')}:
            </BodyText>
            {isColor ? (
              <Flex directionColumn>
                {sortedColorValues.map(pv => (
                  <ColorPropertyValue key={pv.id} value={pv} />
                ))}
              </Flex>
            ) : (
              <Flex wrapContent={true}>
                {propertyValues.map(pv => (
                  <TextPropertyValue key={pv.id} value={pv} />
                ))}
              </Flex>
            )}
          </>
        )}

        {!hasValues && !isAdding && (
          <>
            <Gutter size="sm" />
            <BodyText color="gray500">{t('properties.noValues')}</BodyText>
          </>
        )}
        {isColor && (
          <>
            <Gutter size="sm" />
            {isAdding ? (
              <Flex directionColumn>
                <Div style={{ maxWidth: '800px' }}>
                  <Flex>
                    <Input
                      label={t('properties.labels.valueName')}
                      value={newValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewValue(e.target.value)}
                      disabled={isSaving}
                      placeholder={t('properties.placeholders.valueName')}
                      errorText={nameError}
                    />
                    <Gutter />
                    <Input
                      label={t('properties.labels.valueHex')}
                      value={newInternalValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewInternalValue(e.target.value)}
                      disabled={isSaving}
                      placeholder={t('properties.placeholders.valueHex')}
                      errorText={hexError}
                      maxLength={7}
                    />
                  </Flex>
                </Div>
                <Gutter size="sm" />
                <Flex>
                  <Button size="md" onClick={handleAddSave} disabled={!canSave}>
                    {t('properties.links.saveValue')}
                  </Button>
                  <Gutter />
                  <Button size="md" variant="secondary" onClick={handleAddCancel} disabled={isSaving}>
                    {t('properties.links.cancelValue')}
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Button size="xl" variant="text" textVariantProps={{ color: 'purple500' }} onClick={handleAddStart}>
                <Icon name="plus" margin={{ right: 'xs', top: 'xxs' }} size="sm" />
                {t('properties.links.addColor')}
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
